import { Injectable } from '@angular/core';
import { BehaviorSubject, filter, Observable, Subject, switchMap, tap } from 'rxjs';
import { GetUserUseCase } from '../../../domain/user/use-cases/get-user-use-case';
import { User } from 'common/domain/user/entities/user';

@Injectable({ providedIn: 'root' })
export class UserStore {
    private readonly user$: BehaviorSubject<User | null> = new BehaviorSubject<User | null>(null);
    private readonly loading$: BehaviorSubject<boolean> = new BehaviorSubject(false);
    private readonly isAuthenticated$: BehaviorSubject<boolean> = new BehaviorSubject(false);

    public readonly user: Observable<User | null> = this.user$.asObservable();
    public readonly loading: Observable<boolean> = this.loading$.asObservable();
    public readonly isAuthenticated: Observable<boolean> = this.isAuthenticated$.asObservable();

    private userId: string | null = null;

    public constructor(private readonly getUserUseCase: GetUserUseCase) {
        this.checkAuthenticationStatus();
    }

    public setUserId(userId: string): void {
        this.userId = userId;
    }

    public loadUser(load$: Subject<void>): void {
        load$
            .pipe(
                tap(() => this.loading$.next(true)),
                switchMap(() => {
                    const userId: string | null = this.userId;
                    if (!userId) {
                        this.loading$.next(false);

                        return [];
                    }

                    return this.getUserUseCase.execute({ userId });
                }),
                filter((user: User) => user.attributes && Object.keys(user.attributes).length > 0),
                tap({
                    next: (user: User) => {
                        const newUser: User = new User();
                        newUser.fill(user.toObject());
                        this.user$.next(newUser);
                        this.loading$.next(false);
                    },
                    error: (error: any) => {
                        this.loading$.next(false);

                        throw error;
                    },
                }),
            )
            .subscribe();
    }

    private checkAuthenticationStatus(): void {
        const isAuthenticated: boolean = !!this.userId;
        this.isAuthenticated$.next(isAuthenticated);
    }
}
